import React, { FC, useEffect, useState, KeyboardEvent } from 'react'
import { RoutePaths } from 'routes/paths'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { TextField, Typography, Divider, Tooltip } from '@mui/material'
import { electronicsMpnSearchSelector } from 'store/slices/electronicsMpnSearchSlice'
import { sagaPostElectronicsMpnSearchAction } from 'saga/actions/dataHub'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import PaperWrapper from 'components/custom/PaperWrapper'
import ButtonWrapper from 'components/custom/ButtonWrapper'
import { eButtonTypes, eColor } from 'components/types'
import SingleEntityCO2Values from 'pages/SingleEntity/components/SingleEntityCO2Values'
import { createAccordionEntity, createSingleEntityObj } from 'config/lib'
import { iCo2Emission } from 'store/types'
import { useSearchParams } from 'react-router-dom'
import ThermometerSVG from 'assets/icons/thermometer.svg'
import PackageSizeSVG from 'assets/icons/packageSize.svg'
import WeightSVG from 'assets/icons/weight.svg'
import GlobeSVG from 'assets/icons/globe.svg'

const MpnSearch: FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const result = useSelector(electronicsMpnSearchSelector)
  const [searchParams, setSearchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [input, setInput] = useState('')

  const doSearch = () => {
    if (loading || !input.trim()) return
    setLoading(true)
    dispatch(sagaPostElectronicsMpnSearchAction(input.trim()))
  }

  useEffect(() => {
    if (result) setLoading(false)
  }, [result])

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      doSearch()
    }
  }

  useEffect(() => {
    if (!searchParams.has('contextName')) {
      searchParams.set('contextName', '.electronics')
      setSearchParams(searchParams)
    }
  }, [searchParams, setSearchParams])

  const data = result?.data
  const info = data?.information
  const tech = info?.technical?.mpn
  // const general = info?.general
  const errorStatus = result?.status === 422
  const emissions: iCo2Emission[] = data?.co2Emission || []

  return (
    <ContentBodyWrapper title={t('dataHub.mpnSearch.title')}>
      <PaperWrapper>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
          <TextField
            label={t(
              'dataHub.mpnSearch.placeholder',
              'Type a manufacturer part number…'
            )}
            placeholder={t(
              'dataHub.mpnSearch.placeholder',
              'Type a manufacturer part number…'
            )}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            variant="outlined"
            fullWidth
          />
          <ButtonWrapper
            onClick={doSearch}
            color={eColor.primary}
            disabled={loading || !input.trim()}
            type={eButtonTypes.button}
          >
            {t('dataHub.mpnSearch.search', 'Search')}
          </ButtonWrapper>
        </div>

        {loading && (
          <Typography color="textSecondary">
            {t('matcher.loading', 'Searching…')}
          </Typography>
        )}

        {!loading && !result && (
          <Typography color="textSecondary">
            {t(
              'dataHub.mpnSearch.help',
              'Enter a part number and press Enter or click Search.'
            )}
          </Typography>
        )}

        {!loading && errorStatus && (
          <PaperWrapper className="p-4">
            <Typography variant="h6" color="error">
              {t('dataHub.mpnSearch.notFoundTitle', 'Part Not Found')}
            </Typography>
            <Typography>
              {t(
                'dataHub.mpnSearch.notFoundMessage',
                'The part number you searched for does not exist or could not be resolved. Please try a different value.'
              )}
            </Typography>
          </PaperWrapper>
        )}

        {!loading && data && !errorStatus && (
          <div className="space-y-6">
            <PaperWrapper className="p-4">
              <Typography variant="h6" color={eColor.primary} gutterBottom>
                {t(
                  'dataHub.mpnSearch.generalInfo',
                  'Identification Information'
                )}
              </Typography>
              <Divider className="my-2" />
              <Typography className="mt-3">
                <strong>Manufacturer Part Number (MPN):</strong>{' '}
                {tech?.manufacturerPartNumber}
              </Typography>
              <Typography className="mt-3">
                <strong>Assigned Category:</strong>{' '}
                {info?.identification.description}
              </Typography>
              <Typography className="mt-3">
                <strong>Title:</strong> {info?.identification.title}
              </Typography>
              <Typography className="mt-3">
                <strong>Manufacturer:</strong> {tech?.manufacturer}
              </Typography>
            </PaperWrapper>
            <PaperWrapper>
              <Typography variant="h6" color={eColor.primary} gutterBottom>
                {t('singleEntity.CO2Values.title')}
              </Typography>
              {/* Emission Display using SingleEntityCO2Values */}
              {emissions.map((emission, index) => {
                const singleEntity = createSingleEntityObj(data, '', index)
                const accordionEntity = createAccordionEntity(
                  emission,
                  '.electronics',
                  data
                )
                return (
                  <SingleEntityCO2Values
                    key={`emission-${index}`}
                    emission={emission}
                    singleEntity={singleEntity}
                    entityAccordion={accordionEntity}
                  />
                )
              })}
            </PaperWrapper>
            <PaperWrapper className="p-6">
              <Typography variant="h6" color={eColor.primary} gutterBottom>
                {t('Technical Information')}
              </Typography>
              <Divider className="my-2" />
              <div className="">
                {/* Country of Origin */}
                <div className="flex items-start mt-3">
                  <img
                    src={GlobeSVG}
                    alt="Country"
                    className="mr-3 mt-1 w-6 h-6"
                  />
                  <div>
                    <strong>{t('Country of Origin')}:</strong>{' '}
                    {tech?.countryOfOrigin || t('general.na')}
                  </div>
                </div>

                {/* Weight with comment tooltip */}
                <div className="flex items-start mt-3">
                  <img
                    src={WeightSVG}
                    alt="Weight"
                    className="mr-3 mt-1 w-6 h-6"
                  />
                  <div className="flex items-center flex-wrap">
                    <span>
                      <strong>{t('Weight')}:</strong>{' '}
                      {tech?.weight || t('general.na')} g
                    </span>
                    {tech?.comment && (
                      <Tooltip title={tech.comment} arrow placement="top">
                        <span className="ml-2 text-orange-600 cursor-help font-bold">
                          ?
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>

                {/* Package Size */}
                <div className="flex items-start mt-3">
                  <img
                    src={PackageSizeSVG}
                    alt="Package size"
                    className="mr-3 mt-1 w-6 h-6"
                  />
                  <div>
                    <strong>{t('Package Size')}:</strong>{' '}
                    {tech?.packageSize || t('general.na')}
                  </div>
                </div>

                {/* Operating Temperature Range */}
                <div className="flex items-start mt-3">
                  <img
                    src={ThermometerSVG}
                    alt="Operating Temperature Range"
                    className="mr-3 mt-1 w-6 h-6"
                  />
                  <div>
                    <strong>{t('Operating Temperature Range')}:</strong>{' '}
                    {tech?.operatingTemperatureRange || t('general.na')}
                  </div>
                </div>
              </div>
            </PaperWrapper>
          </div>
        )}
      </PaperWrapper>
    </ContentBodyWrapper>
  )
}

export default {
  routeProps: {
    path: RoutePaths.toolboxMpnSearch,
    title: 'dataHub.mpnSearch.title',
    element: <MpnSearch />,
    roles: componentPermissions[componentNames.pageElectronicsMpnSearch],
    requiresAuthentication: true,
  },
  name: 'Electronics MPN Search',
}
